import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { appContextResolver } from 'app/core/app/app-context.resolver';
import { sectionEnabledGuard } from 'app/core/guards/section-enabled.guard';
import {
  disabledSectionNotice,
  isSectionEnabled,
} from 'app/core/environment/build-features';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

/**
 * Where `/projects/:projectId` lands. Normally the Manage area (which opens on
 * the project Overview); on a build without it — staging, where Manage is
 * Payment-backed — Fleet, the one section such a build has.
 */
const defaultProjectSection = () =>
  isSectionEnabled('project-settings') ? 'settings' : 'fleet';

export const appRoutes: Route[] = [
  //Redirect empty path to '/projects'
  { path: '', pathMatch: 'full', redirectTo: 'projects' },

  // Redirect signed-in user to the '/projects'
  //
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'projects' },

  // Auth routes for guests
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'confirmation-required',
        loadChildren: () =>
          import('app/modules/auth/confirmation-required/confirmation-required.routes'),
      },
      {
        path: 'forgot-password',
        loadChildren: () =>
          import('app/modules/auth/forgot-password/forgot-password.routes'),
      },
      {
        path: 'reset-password',
        loadChildren: () =>
          import('app/modules/auth/reset-password/reset-password.routes'),
      },
      {
        path: 'sign-in',
        loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes'),
      },
      {
        path: 'sign-up',
        loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes'),
      },
    ],
  },

  {
    path: '',
    component: LayoutComponent,
    data: { layout: 'empty' },
    children: [
      {
        path: 'mcp/connect',
        canActivate: [
          (route, state) =>
            route.queryParamMap.has('failure') ? true : AuthGuard(route, state),
        ],
        loadComponent: () =>
          import('app/modules/auth/mcp-connect/mcp-connect.component').then(
            (m) => m.McpConnectComponent,
          ),
      },
    ],
  },

  // Auth routes for authenticated users
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'sign-out',
        loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes'),
      },
      {
        path: 'unlock-session',
        loadChildren: () =>
          import('app/modules/auth/unlock-session/unlock-session.routes'),
      },
      {
        path: 'service-unavailable',
        loadComponent: () =>
          import('app/core/components/service-unavailable/service-unavailable.component').then(
            (m) => m.ServiceUnavailableComponent,
          ),
      },
    ],
  },

  // Landing routes
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'home',
        loadChildren: () => import('app/modules/landing/home/home.routes'),
      },
    ],
  },

  // Admin routes
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      // Project list - shows all available projects
      {
        path: 'projects',
        loadChildren: () => import('app/modules/admin/apps/apps.routes'),
      },

      // Explains a section this build switched off (see
      // `environment.enabledSections`). Registered only when the build defines
      // the copy for it — `environment.ts` sets `disabledSectionNotice: null`,
      // so release builds have neither this route nor the text it renders.
      ...(disabledSectionNotice()
        ? [
            {
              path: 'unavailable/:section',
              loadComponent: () =>
                import('app/core/components/section-unavailable/section-unavailable.component').then(
                  (m) => m.SectionUnavailableComponent,
                ),
            },
          ]
        : []),

      // User settings (tenant-level, not project-scoped)
      {
        path: 'settings',
        loadChildren: () =>
          import('app/modules/admin/user-settings/user-settings.routes'),
      },

      // Project-scoped routes - all features that are scoped to a specific project
      {
        path: 'projects/:projectId',
        resolve: { project: appContextResolver },
        children: [
          // Default redirect: the Manage area when this build has it, Fleet
          // otherwise. A redirect into a section the build disabled would only
          // bounce the user straight back out through its guard.
          {
            path: '',
            redirectTo: defaultProjectSection(),
            pathMatch: 'full',
          },
          {
            path: 'settings',
            canActivate: [sectionEnabledGuard('project-settings')],
            loadChildren: () =>
              import('app/modules/admin/app-settings/app-settings.routes'),
          },
          {
            path: 'voice',
            canActivate: [sectionEnabledGuard('voice')],
            loadChildren: () => import('app/modules/admin/voice/voice.routes'),
          },
          {
            path: 'fleet',
            canActivate: [sectionEnabledGuard('fleet')],
            loadChildren: () =>
              import('app/modules/admin/fleet/fleet.routes').then(
                (m) => m.FLEET_ROUTES,
              ),
          },
          {
            path: 'cortex',
            canActivate: [sectionEnabledGuard('cortex')],
            loadChildren: () =>
              import('app/modules/admin/cortex/cortex.routes'),
          },
          {
            path: 'rooms',
            canActivate: [sectionEnabledGuard('rooms')],
            loadChildren: () => import('app/modules/admin/rooms/rooms.routes'),
          },
        ],
      },
    ],
  },

  // Catch-all. Without this, an unknown URL produces a `NavigationError` instead
  // of a `NavigationEnd`, and the splash screen — which hides on the first
  // `NavigationEnd` — never goes away, leaving the user on a spinner forever.
  //
  // Redirect straight to `projects`, NOT to `''`. A redirect whose target is
  // itself a redirect does not chain: `''` would resolve, stop there, match a
  // bare `path: ''` parent and render `LayoutComponent` with an empty outlet —
  // a blank page instead of the sign-in form. `projects` is the real
  // destination, and the AuthGuard on it sends guests to sign-in.
  { path: '**', redirectTo: 'projects' },
];
